import type { DisplayObject } from '@suntex.classic/createjs';
import { UIUtil } from '@suntex.core/animation';
import { LogLevel, Logger, logged } from '@suntex.core/logging';

import type { HubSettings } from '../types';
import { withFromParam } from '../utils/withFromParam';

import type { InfoPane } from './InfoPane';
import { Radial } from './Radial';
import type { IHub, IRadial } from './types';
const logger = Logger.for(() => Hub, { level: LogLevel.Debug });

const ANIMATION_MS = 250;
const INFOPANE_ALPHA_ANIMATION_FACTOR = 4;

export class Hub extends createjs.Container implements IHub {
  readonly root: Radial;
  public centralRadial: IRadial | null = null;
  private infoPane: InfoPane | null = null;
  public centerX: number = 0;
  public centerY: number = 0;
  public onCentralize: ((radial: IRadial) => void) | undefined = undefined;
  private centralizeListeners = new Set<(radial: IRadial) => void>();
  private radialsOnScreen: Set<IRadial> = new Set();

  /**
   * Unique per-instance scope symbol for tracking delays that should be cancelled on dispose.
   * Pass this to UIUtil.withDelay() calls that should be cleaned up with the Hub.
   */
  readonly delayScope: symbol = Symbol('hub-delays');

  animationDurationFactor = 1;

  constructor(
    rootRadial: Radial,
    centerX: number,
    centerY: number,
    public config: {
      setPath?: (path: string) => void;
      navigate?: (href: string) => void;
    } = {},
  ) {
    super();
    rootRadial.hub = this;
    this.root = rootRadial;

    this.x = this.centerX = centerX;
    this.y = this.centerY = centerY;

    this.infoPane = null;
  }

  addChild<T extends DisplayObject>(child: T): T;
  addChild<T extends DisplayObject>(child0: DisplayObject, lastChild: T): T;
  addChild<T extends DisplayObject>(
    child0: DisplayObject,
    child1: DisplayObject,
    lastChild: T,
  ): T;
  addChild<T extends DisplayObject>(
    child0: DisplayObject,
    child1: DisplayObject,
    child2: DisplayObject,
    lastChild: T,
  ): T;
  public override addChild<T extends DisplayObject>(...children: T[]): T {
    const result = super.addChild(...children);
    for (const child of children) {
      if (child instanceof Radial) {
        this.radialsOnScreen.add(child);
      }
    }
    return result as T;
  }

  public override removeChild(...children: DisplayObject[]): boolean {
    const result = super.removeChild(...children);
    for (const child of children) {
      if (child instanceof Radial) {
        this.radialsOnScreen.delete(child);
      }
    }
    return result;
  }

  @logged({ logger })
  public applyHubSettings(settings: HubSettings): void {
    this.animationDurationFactor = settings.disableAnimation
      ? 0
      : settings.animationSpeed
        ? 1 / settings.animationSpeed
        : 1;
    this.root.setDisplaySettings(settings);
    this.root.radialDescendents.forEach((radial) => {
      radial.setDisplaySettings(settings);
    });
  }

  public clickRadial(radial: IRadial): void {
    // Verify and clean up any stray radials that shouldn't be on screen
    this.verifyRadialsOnScreen();

    const href = radial.href;
    logger.forMethod(this.clickRadial).trace({ href, tag: radial.tag });

    if (href) {
      const hrefWithFrom = withFromParam(
        href,
        this.centralRadial?.getFullPath(),
      );
      const { navigate } = this.config;

      if (navigate) {
        navigate(hrefWithFrom);
      } else {
        UIUtil.navigate(hrefWithFrom);
      }
    } else {
      let target = radial;

      if (radial === this.centralRadial && radial.radialParent) {
        target = radial.radialParent;
      } else if (this.centralRadial?.radialChildren.includes(radial)) {
        const enabledAndInFocusChildren = radial.radialChildren.filter(
          (r) => r.enabled && r.inFocus,
        );
        const firstEnabledChild = enabledAndInFocusChildren[0];

        if (
          enabledAndInFocusChildren.length === 1 &&
          firstEnabledChild &&
          firstEnabledChild.children.length &&
          !firstEnabledChild.href
        ) {
          target = firstEnabledChild;
        }
      }

      if (this.config.setPath) {
        this.config.setPath(target.getFullPath());
      } else {
        this.centralizeRadial(target);
      }
    }
  }

  public subscribeCentralize(listener: (radial: IRadial) => void): () => void {
    this.centralizeListeners.add(listener);
    return () => this.centralizeListeners.delete(listener);
  }

  @logged({ logger })
  public centralizeRadial(radial: IRadial): void {
    const nextCentralRadial = radial;
    const previousCentralRadial = this.centralRadial;

    this.onCentralize?.(radial);
    for (const listener of this.centralizeListeners) {
      listener(radial);
    }

    if (nextCentralRadial === previousCentralRadial) {
      return;
    } else {
      this.centralRadial = nextCentralRadial;
    }

    const animationMs = this.animationDurationFactor * ANIMATION_MS;
    const offset = {
      offsetX: nextCentralRadial.moveX,
      offsetY: nextCentralRadial.moveY,
    };

    UIUtil.stopAllTweensAndDelays();

    // Ensure breadcrumb ancestors have correct scale after animation interruption
    for (const ancestor of nextCentralRadial.radialAncestors) {
      ancestor.finalizeTweens();
    }

    // remove previous radials that are not the new central radial, or ancestors or children of the new central radial
    if (previousCentralRadial) {
      for (const radial of [
        previousCentralRadial,
        ...previousCentralRadial.radialAncestors,
        ...previousCentralRadial.radialChildren,
      ]) {
        if (
          radial !== nextCentralRadial &&
          !nextCentralRadial.radialAncestors.includes(radial) &&
          !nextCentralRadial.radialChildren.includes(radial)
        ) {
          radial.hide({ animationMs });
          UIUtil.withDelay(() => this.removeChild(radial), {
            ms: animationMs,
            scope: this.delayScope,
          })();
        }
      }
    }

    this.changeInfoPane(nextCentralRadial.infoPane, { animationMs });

    // move the new central radial to the center
    if (nextCentralRadial.parent === this) {
      // If the radial is already displayed
      this.setChildIndex(nextCentralRadial, this.numChildren - 1);
      nextCentralRadial.moveToCenter({ animationMs, ...offset });
    } else {
      this.addChild(nextCentralRadial);
      nextCentralRadial.appearInCenter({ animationMs, ...offset });
    }

    // show breadcrumbs
    const ancestors = nextCentralRadial.radialAncestors;
    ancestors.forEach((radial, i) => {
      this.addChild(radial);
      radial.moveToBreadcrumb({
        animationMs,
        depth: i,
        isSole: ancestors.length === 1,
      });
    });

    // show radial children
    let n = 0;
    for (const radial of [...nextCentralRadial.radialChildren].reverse()) {
      n++;
      if (radial.display) {
        if (radial === previousCentralRadial) {
          radial.moveFromCenter({ animationMs, ...offset });
        } else {
          radial.emergeFromCenter({
            animationMs,
            delayMs:
              animationMs / 2 +
              (n * animationMs) / nextCentralRadial.radialChildren.length,
            ...offset,
          });
          this.addChild(radial);
        }
      }
    }

    this.setChildIndex(nextCentralRadial, this.numChildren - 1);
  }

  private verifyRadialsOnScreen(): void {
    if (!this.centralRadial) return;

    const expectedRadials = new Set<IRadial>([
      this.centralRadial,
      ...this.centralRadial.radialAncestors,
      ...this.centralRadial.radialChildren,
    ]);

    // Find and remove any radials that shouldn't be on screen
    const strayRadials: IRadial[] = [];
    for (const radial of this.radialsOnScreen) {
      if (!expectedRadials.has(radial)) {
        strayRadials.push(radial);
      }
    }

    if (strayRadials.length > 0) {
      logger.forMethod(this.verifyRadialsOnScreen).warn('Found stray radials', {
        count: strayRadials.length,
        tags: strayRadials.map((r) => r.tag),
      });

      for (const radial of strayRadials) {
        radial.hide({ animationMs: 0 });
        this.removeChild(radial);
      }
    }
  }

  private changeInfoPane(
    infoPane: InfoPane | null,
    { animationMs }: { animationMs: number },
  ): void {
    // if an infopane is present, remove it
    if (this.infoPane) {
      this.removeChild(this.infoPane);
      this.infoPane = null;
    }

    // add new infopane
    if (infoPane) {
      this.infoPane = infoPane;
      this.addChildAt(infoPane, 0);
      infoPane.alpha = 0.0;
      UIUtil.tween(
        infoPane,
        'alpha',
        1.0,
        animationMs * INFOPANE_ALPHA_ANIMATION_FACTOR,
        'strongInOut',
      );
    }
  }

  /**
   * Cleanup method to remove all radials and event listeners.
   * Call this before removing the hub from the stage to prevent phantom interactions.
   */
  @logged({ logger })
  public dispose(): void {
    // Cancel all pending delays scoped to this Hub
    UIUtil.cancelDelaysForScope(this.delayScope);

    // Stop all tweens on hub-managed objects
    this.stopAllHubTweens();

    // Destroy all radials (removes their event listeners)
    this.root.destroy();

    // Remove all children from the hub
    this.removeAllChildren();

    // Clear the radials set
    this.radialsOnScreen.clear();

    // Clear references
    this.centralRadial = null;
    this.infoPane = null;
    this.onCentralize = undefined;
    this.centralizeListeners.clear();
  }

  /**
   * Stop all tweens on objects managed by this Hub.
   * This includes all radials, their buttons, stems, and the info pane.
   */
  private stopAllHubTweens(): void {
    // Stop tweens on all radials (root + descendants)
    const allRadials = [this.root, ...this.root.radialDescendents];
    for (const radial of allRadials) {
      UIUtil.removeObjectTweens(radial);
      UIUtil.removeObjectTweens(radial.button);
      UIUtil.removeObjectTweens(radial.stem);
    }

    // Stop tweens on info pane if present
    if (this.infoPane) {
      UIUtil.removeObjectTweens(this.infoPane);
    }
  }
}
